import { forwardRef, useCallback, type InputHTMLAttributes, type ReactNode, type WheelEvent } from 'react';
import { cn } from '../../lib/cn';
import Field, { controlClass, type ControlSize } from './Field';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  hint?: string;
  error?: string;
  /** Control height ('sm' 28 / 'md' 32 / 'lg' 36). A number is passed through
   *  as the native HTML `size` attribute for backward compatibility. */
  size?: ControlSize | number;
  /** Leading adornment, e.g. "$" or <Search size={16}/>. */
  leading?: ReactNode;
  /** Trailing adornment, e.g. "%". */
  trailing?: ReactNode;
}

const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, required, size = 'lg', leading, trailing, className, id, ...rest },
  ref,
) {
  const controlSize: ControlSize = typeof size === 'string' ? size : 'lg';
  const htmlSize = typeof size === 'number' ? size : undefined;
  return (
    <Field label={label} hint={hint} error={error} required={required} id={id}>
      {(ids) => (
        <div className="relative">
          {leading && (
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-ink-faint">
              {leading}
            </span>
          )}
          <input
            ref={ref}
            id={ids.id}
            aria-describedby={ids.describedBy}
            aria-invalid={ids.invalid || undefined}
            required={required}
            size={htmlSize}
            className={cn(controlClass(controlSize, ids.invalid), leading && 'pl-7', trailing && 'pr-8', className)}
            {...rest}
          />
          {trailing && (
            <span className="absolute inset-y-0 right-3 flex items-center text-sm text-ink-faint">
              {trailing}
            </span>
          )}
        </div>
      )}
    </Field>
  );
});

export default Input;

/** Money entry: $ prefix, right-aligned tabular digits, scroll-wheel guard. */
export const MoneyInput = forwardRef<HTMLInputElement, InputProps>(function MoneyInput(
  { className, ...props },
  ref,
) {
  const blurOnWheel = useCallback((e: WheelEvent<HTMLInputElement>) => {
    e.currentTarget.blur();
  }, []);
  return (
    <Input
      ref={ref}
      type="number"
      inputMode="decimal"
      step="0.01"
      min="0"
      leading="$"
      onWheel={blurOnWheel}
      className={cn('text-right', className)}
      {...props}
    />
  );
});

/** Plain number entry with the scroll-wheel guard. */
export const NumberInput = forwardRef<HTMLInputElement, InputProps>(function NumberInput(props, ref) {
  const blurOnWheel = useCallback((e: WheelEvent<HTMLInputElement>) => {
    e.currentTarget.blur();
  }, []);
  return <Input ref={ref} type="number" inputMode="numeric" onWheel={blurOnWheel} {...props} />;
});
